import { useQuery } from "@apollo/client";
import { AllInclusiveOutlined } from "@mui/icons-material";
import { Chip } from "@mui/material";
import { upperFirst } from "lodash";
import { DateTime } from "luxon";
import { ReactNode, useState } from "react";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";

import { ListAllBannersQuery } from "~frontend/__generated__/graphql";
import { LIST_ALL_BANNERS } from "~frontend/graphql/banners";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";

import { AsyncWrapper } from "../AsyncWrapper";
import { ExternalLink } from "../Link";
import PageContainer from "../PageContainer";
import Table, { TableBox, TableControls } from "../Table";

export const Banners = (): ReactNode => {
  const isStaff = useCurrentUserIsStaff();

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );

  const [activeFilter, setActiveFilter] = useState<"active" | "all">("all");

  const {
    data: banners,
    error,
    loading,
    refetch,
  } = useQuery<ListAllBannersQuery>(LIST_ALL_BANNERS, {
    variables: {
      input: {
        onlyActive: activeFilter === "active",
      },
    },
  });

  if (!isStaff) {
    return null;
  }

  return (
    <PageContainer maxWidth="lg">
      <TableControls
        filters={[
          {
            id: "active",
            label: "Status",
            value: activeFilter,
            onChange: (value: string) => {
              setActiveFilter(value as "active" | "all");
              void refetch();
            },
            options: [
              { value: "active", label: "Active" },
              { value: "all", label: "All" },
            ],
          },
        ]}
        addButtons={
          [
            // TODO
            // {
            //   label: "Add Banner",
            //   onClick: () => setAddBannerDialog(true),
            //   id: "add-banner",
            // },
          ]
        }
      />
      <TableBox sx={{ pb: 1 }}>
        <AsyncWrapper
          data={banners?.listAllBanners}
          errors={error}
          loading={loading}
        >
          {banners => (
            <Table<ListAllBannersQuery["listAllBanners"][0]>
              columns={[
                {
                  key: "id",
                  title: "Audience",
                  renderValue: ({ tenant, subaccount, userEmail }) => {
                    if (!tenant && !subaccount && !userEmail) {
                      return "Global";
                    } else if (userEmail) {
                      return `User: ${userEmail}`;
                    } else if (tenant && !subaccount) {
                      return `Tenant: ${tenant.name} (${tenant.slug})`;
                    } else if (tenant && subaccount) {
                      return `Tenant: ${tenant.name} (${tenant.slug}), Subaccount: ${subaccount.name} (${subaccount.slug})`;
                    }
                  },
                },
                {
                  key: "level",
                  title: "Level",
                  renderValue: value => (
                    <Chip
                      color={
                        {
                          INFO: "primary",
                          WARNING: "warning",
                          ERROR: "error",
                        }[value.level] as "primary" | "warning" | "error"
                      }
                      label={upperFirst(value.level.toLocaleLowerCase())}
                    />
                  ),
                },
                {
                  key: "message",
                  title: "Message",
                  sx: { minWidth: 400 },
                  align: "left",
                  renderValue: value => value.message,
                },
                {
                  key: "link",
                  title: "Link",
                  renderValue: value =>
                    value.link ? (
                      <ExternalLink href={value.link}>
                        {value.link}
                      </ExternalLink>
                    ) : (
                      "--"
                    ),
                },
                {
                  key: "linkText",
                  title: "Link Text",
                  renderValue: value =>
                    value.linkText ? value.linkText : "--",
                },
                {
                  key: "startsAt",
                  title: "Active Dates",
                  renderValue: value => {
                    const startsAtString = DateTime.fromMillis(
                      value.startsAt
                    ).toLocaleString(DateTime.DATETIME_SHORT);
                    const expiresAtString = value.expiresAt
                      ? DateTime.fromMillis(value.expiresAt).toLocaleString(
                          DateTime.DATETIME_SHORT
                        )
                      : null;

                    return (
                      <>
                        {startsAtString} -{" "}
                        {expiresAtString ? (
                          expiresAtString
                        ) : (
                          <AllInclusiveOutlined />
                        )}
                      </>
                    );
                  },
                },
                {
                  key: "createdAt",
                  title: "Created At",
                  renderValue: value =>
                    DateTime.fromMillis(value.createdAt).toLocaleString(),
                },
                {
                  key: "createdByEmail",
                  title: "Created By",
                  renderValue: value => value.createdByEmail,
                },
              ]}
              rows={banners}
              rowsPerPage={pageSize}
              onChangeRowsPerPage={newRowsPerPage => {
                setPageSize(newRowsPerPage);
                setPage(0);
              }}
              page={page}
              onChangePage={setPage}
              totalCount={banners.length}
              emptyState="No banners found."
            />
          )}
        </AsyncWrapper>
      </TableBox>
    </PageContainer>
  );
};
